import React, { FC, Fragment, MouseEvent, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EastIcon from '@mui/icons-material/East';
import { ClinicDeliveryOrders } from 'models/transport';
import DeliveryDetails from 'components/orders/shared/delivery-details.component';
import { Order } from 'models/order';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Organization } from 'models/organization';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { LoadingButton } from '@mui/lab';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation } from '@apollo/client';
import { unsetOrderReadyForDeliveryTemplate } from 'apollo/templates/transport/mutation.graphql';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';

type OrdersReadyForTransportProps = {
    clinicOrders: ClinicDeliveryOrders[];
    addedOrders: Order[];
    currentClinic: Organization | undefined;
    addOrder: (clinic: Organization, order: Order) => void;
    refetchAfterRemoveDelivery: () => void;
};
const OrdersReadyForTransport: FC<OrdersReadyForTransportProps> = ({
    clinicOrders,
    addedOrders,
    currentClinic,
    addOrder,
    refetchAfterRemoveDelivery,
}: OrdersReadyForTransportProps) => {
    const { t: translate } = useTranslation(['transport', 'common', 'orders']);
    const [removeDeliveryConfirmDialogOpen, setRemoveDeliveryConfirmDialogOpen] = useState(false);
    const [orderId, setOrderId] = useState('');

    const [unsetOrderReadyForDelivery, { loading: isUnsetLoading, error: unsetError }] = useMutation(
        unsetOrderReadyForDeliveryTemplate,
        {
            onError: (err) => {
                console.log(err, 'error');
            },
            onCompleted: () => {
                refetchAfterRemoveDelivery();
                setRemoveDeliveryConfirmDialogOpen(false);
                setOrderId('');
            },
        },
    );

    const isNotCurrentClinic = (clinicOrders: ClinicDeliveryOrders) => {
        return currentClinic && currentClinic._id !== clinicOrders._id;
    };

    const handleDeleteClick = (orderId: string, event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setOrderId(orderId as string);
        setRemoveDeliveryConfirmDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setRemoveDeliveryConfirmDialogOpen(false);
        setOrderId('');
    };

    const handleConfirmDelete = () => {
        unsetOrderReadyForDelivery({
            variables: { orderId: orderId },
        });
    };

    return (
        <Fragment>
            {unsetError ? <AlertSnackbar type="error" message={unsetError.message} /> : undefined}
            {clinicOrders.length === 0 ? (
                <Alert severity="info">{translate('transport:NO_ORDERS_READY_FOR_DELIVERY')}</Alert>
            ) : (
                <Fragment>
                    {clinicOrders.map((clinicOrder) => (
                        <Card
                            key={clinicOrder._id}
                            sx={{ marginBottom: 2, opacity: isNotCurrentClinic(clinicOrder) ? 0.5 : 1 }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <Box flexGrow={1}>
                                        <Typography gutterBottom variant="h5" component="div" sx={{ flexGrow: 1 }}>
                                            {clinicOrder.clinic.name}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <img
                                            style={{ maxHeight: 20 }}
                                            src={`/images/transport-logos/${clinicOrder.transportProvider.externalId}.png`}
                                        />
                                    </Box>
                                </Box>

                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                            <TableCell>{translate('orders:PATIENT')}</TableCell>
                                            <TableCell>{translate('orders:DENTIST')}</TableCell>
                                            <TableCell>{translate('orders:DELIVERY')}</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clinicOrder.orders.map((order) => (
                                            <TableRow
                                                key={order._id}
                                                hover
                                                onClick={() =>
                                                    !addedOrders.includes(order)
                                                        ? !isNotCurrentClinic(clinicOrder)
                                                            ? addOrder(clinicOrder.clinic, order)
                                                            : undefined
                                                        : undefined
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell sx={{ width: 150 }}>
                                                    <LoadingButton
                                                        size="small"
                                                        onClick={(event) => handleDeleteClick(order._id, event)}
                                                        disabled={
                                                            order.isDeliveryCreated || addedOrders.includes(order)
                                                        }
                                                        startIcon={<DeleteIcon />}
                                                        loading={isUnsetLoading}
                                                    >
                                                        {translate('common:REMOVE')}
                                                    </LoadingButton>
                                                </TableCell>
                                                <TableCell>
                                                    <span>{order.orderId}</span>
                                                    {order.internalOrderId ? (
                                                        <span>{order.internalOrderId}</span>
                                                    ) : undefined}
                                                </TableCell>
                                                <TableCell className="mouseflow-mask">
                                                    <Box>
                                                        {formatPatientName(
                                                            order.patient,
                                                            translate('orders:NO_PATIENT_REFERENCE'),
                                                        )}
                                                    </Box>
                                                    {order.patient?.personalIdentityNumber ? (
                                                        <Typography variant="caption">
                                                            {order.patient?.personalIdentityNumber}
                                                        </Typography>
                                                    ) : order.patient ? (
                                                        <Typography variant="caption">
                                                            {translate('orders:NO_PIN_NUMBER')}
                                                        </Typography>
                                                    ) : undefined}
                                                </TableCell>
                                                <TableCell>
                                                    {order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}
                                                </TableCell>
                                                <TableCell>
                                                    <DeliveryDetails details={order.deliveryDetails} />
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: 150 }}>
                                                    {!addedOrders.includes(order) ? (
                                                        <Button
                                                            size="small"
                                                            endIcon={<EastIcon />}
                                                            onClick={() => addOrder(clinicOrder.clinic, order)}
                                                            disabled={
                                                                order.isDeliveryCreated ||
                                                                isNotCurrentClinic(clinicOrder)
                                                            }
                                                        >
                                                            {translate('common:ADD')}
                                                        </Button>
                                                    ) : (
                                                        <CheckCircleIcon fontSize="small" color="success" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    ))}
                </Fragment>
            )}
            {removeDeliveryConfirmDialogOpen && (
                <ConfirmDialog
                    open={removeDeliveryConfirmDialogOpen || false}
                    handleClose={handleCloseDialog}
                    dialogTitle={translate('transport:REMOVE_DELIVERY')}
                    dialogContent={translate('transport:CONFIRM_REMOVE_DELIVERY')}
                    handleConfirm={handleConfirmDelete}
                />
            )}
        </Fragment>
    );
};

export default OrdersReadyForTransport;
